import { GoBoxStyled } from '@OnArk/styles/Boxes.styled';
import { ChurchButtonStyled } from '@OnArk/styles/Buttons.styled';
import { TextStyled, TitleStyled } from '@OnArk/styles/Text.styled';

import React from 'react';

import Link from 'next/link';

import { MaxWidthStyled } from '@hangar31/rc-layout';
import Icon from '@OnArk/SVGs/Icon';
import { useRichText } from '@OnArk/utils/useRichText';
import withAuth from 'graphql/withAuth';
import withClient from 'graphql/withClient';
import { currentChurchUrlFor } from 'server/churchUrlAlias';

function ChurchCourseDirectory({ church }: ChurchCourseDirectoryPageProps) {
  const richText = useRichText();

  return (
    <div css={{ marginBottom: '20vh' }}>
      <TitleStyled>Class Directory</TitleStyled>
      <MaxWidthStyled grid>
        {church.courses.map(course => (
          <GoBoxStyled key={course.id}>
            <h1>
              {course.name}
              <span>
                <ChurchButtonStyled
                  inline
                  as={Link}
                  bottom="1.5"
                  href={`/${church.url}/${course.url}`}
                  top="1.5"
                >
                  Go
                </ChurchButtonStyled>
                <Icon name="ARROWS_Right" right="0" size="20" />
              </span>
            </h1>
            {course.description?.replace(/<\/?[a-zA-Z]+>/gm, '') && (
              <TextStyled
                dangerouslySetInnerHTML={{
                  __html: richText(course.description),
                }}
              />
            )}
          </GoBoxStyled>
        ))}
      </MaxWidthStyled>
    </div>
  );
}

export type ChurchCourseDirectoryPageProps = ServerSidePagePropsWithClient<
  typeof getServerSideProps
>;
export const getServerSideProps = withAuth(async ({ client, params }) => {
  const churchUrl = params?.['church.url'] as string;
  const { church } = await client.getChurchByUrl({
    churchUrl,
    date: new Date().toISOString(),
  });

  if (!church) {
    // A church renamed its URL slug (admin settings) — an old link should
    // redirect to the new one instead of 404ing forever. See churchUrlAlias.ts.
    //
    // 307, not 308: the unique index covers only the LIVE url, so a retired
    // slug can later be claimed by a DIFFERENT church. A permanently cached
    // redirect would then strand those visitors on the wrong church forever,
    // because their browser would never ask for the old slug again.
    const currentUrl = await currentChurchUrlFor(churchUrl);

    if (currentUrl) {
      return {
        redirect: { destination: `/${currentUrl}`, permanent: false },
      };
    }

    return { notFound: true };
  }

  return {
    props: {
      church: church,
      layout: 'GUEST',
      pageTitle: `${church?.name} | OnArk`,
    },
  };
}, 'PUBLIC');

export default withClient(ChurchCourseDirectory);
